/**
 * OpenStreetMap building footprints → local-meter extrusions for site context.
 * See docs/ADU_SITE_PLANNER.md
 */

const OVERPASS_URL = 'https://overpass-api.de/api/interpreter';
const MAX_BUILDINGS = 350;
const DEFAULT_HEIGHT_M = 6;
const METERS_PER_DEG_LAT = 111320;

export function metersPerDegLng(lat) {
  return METERS_PER_DEG_LAT * Math.cos((lat * Math.PI) / 180);
}

/** Local scene meters: +X east, +Z south (so −Z is north, matching north-up sat). */
export function latLngToLocal(lat, lng, originLat, originLng) {
  const x = (lng - originLng) * metersPerDegLng(originLat);
  const z = (originLat - lat) * METERS_PER_DEG_LAT;
  return [x, z];
}

function parseHeightMeters(tags = {}) {
  if (tags.height) {
    const h = parseFloat(String(tags.height).replace(/m$/i, ''));
    if (Number.isFinite(h) && h > 0 && h < 500) return h;
  }
  if (tags['building:levels']) {
    const levels = parseFloat(tags['building:levels']);
    if (Number.isFinite(levels) && levels > 0) return Math.min(levels * 3, 200);
  }
  return DEFAULT_HEIGHT_M;
}

/**
 * @param {number} lat
 * @param {number} lng
 * @param {number} halfExtentMeters half of ground square side
 * @returns {Promise<Array<{ id: string|number, points: number[][], height: number }>>}
 */
export async function fetchOsmBuildings(lat, lng, halfExtentMeters) {
  const dLat = halfExtentMeters / METERS_PER_DEG_LAT;
  const dLng = halfExtentMeters / metersPerDegLng(lat);
  const south = lat - dLat;
  const north = lat + dLat;
  const west = lng - dLng;
  const east = lng + dLng;

  const query = `
[out:json][timeout:45];
(
  way["building"](${south},${west},${north},${east});
);
out body;
>;
out skel qt;
`.trim();

  const res = await fetch(OVERPASS_URL, {
    method: 'POST',
    headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
    body: `data=${encodeURIComponent(query)}`,
  });
  if (!res.ok) {
    throw new Error(`Overpass HTTP ${res.status}`);
  }
  const data = await res.json();
  return parseOsmBuildings(data, lat, lng).slice(0, MAX_BUILDINGS);
}

export function parseOsmBuildings(data, originLat, originLng) {
  const nodes = new Map();
  for (const el of data.elements || []) {
    if (el.type === 'node') nodes.set(el.id, el);
  }

  const buildings = [];
  for (const el of data.elements || []) {
    if (el.type !== 'way' || !el.tags?.building || !el.nodes?.length) continue;
    const ring = [];
    for (const nid of el.nodes) {
      const n = nodes.get(nid);
      if (!n) continue;
      ring.push(latLngToLocal(n.lat, n.lon, originLat, originLng));
    }
    if (ring.length < 3) continue;
    // Drop closing duplicate if present
    const a = ring[0];
    const b = ring[ring.length - 1];
    if (a[0] === b[0] && a[1] === b[1]) ring.pop();
    if (ring.length < 3) continue;

    buildings.push({
      id: el.id,
      points: ring,
      height: parseHeightMeters(el.tags),
    });
  }
  return buildings;
}

/** Axis-aligned rectangle vertices (CCW on XZ), size in meters. */
export function rectangleVertices(centerX, centerZ, width, depth) {
  const hx = width / 2;
  const hz = depth / 2;
  return [
    [centerX - hx, centerZ - hz],
    [centerX + hx, centerZ - hz],
    [centerX + hx, centerZ + hz],
    [centerX - hx, centerZ + hz],
  ];
}

export function polygonCentroid(vertices) {
  if (!vertices?.length) return [0, 0];
  let sx = 0;
  let sz = 0;
  for (const [x, z] of vertices) {
    sx += x;
    sz += z;
  }
  return [sx / vertices.length, sz / vertices.length];
}
